import React, { Dispatch, SetStateAction } from "react";
import { Download } from "lucide-react";

export interface DocumentPopUpProps {
  showDoc: boolean;
  setShowDoc: Dispatch<SetStateAction<boolean>> | ((val: boolean) => void);
  docs?: string | null;
  url?: string | null;
  fileName?: string;
  fileType?: string;
}

const fileIconMap: { [extension: string]: string } = {
  doc: "https://w7.pngwing.com/pngs/89/291/png-transparent-microsoft-word-logo-microsoft-word-office-open-xml-document-computer-icons-computer-file-word-file-icon-blue-angle-text-thumbnail.png",
  docx: "https://w7.pngwing.com/pngs/89/291/png-transparent-microsoft-word-logo-microsoft-word-office-open-xml-document-computer-icons-computer-file-word-file-icon-blue-angle-text-thumbnail.png",
  docs: "https://w7.pngwing.com/pngs/89/291/png-transparent-microsoft-word-logo-microsoft-word-office-open-xml-document-computer-icons-computer-file-word-file-icon-blue-angle-text-thumbnail.png",
  xls: "https://cdn-icons-png.flaticon.com/512/732/732220.png",
  xlsx: "https://cdn-icons-png.flaticon.com/512/732/732220.png",
  pdf: "https://cdn-icons-png.flaticon.com/512/4208/4208479.png",
};

const defaultFileIcon =
  "https://cdn4.iconfinder.com/data/icons/essentials-72/24/007_-_File-512.png";

const getDocumentFileIcon = (path: string) => {
  const match = path.match(/\.(\w+)(?:\?|#|$)/i);
  if (match) {
    const ext = match[1].toLowerCase();
    return fileIconMap[ext] || defaultFileIcon;
  }
  return defaultFileIcon;
};

const resolveFileUrl = (src?: string | null) => {
  if (!src) return "";
  const clean = src.replace(/\\/g, "/");
  if (
    clean.startsWith("http://") ||
    clean.startsWith("https://") ||
    clean.startsWith("blob:") ||
    clean.startsWith("data:")
  ) {
    return clean;
  }
  const baseUrl = (process.env.REACT_APP_Url || "").replace(/\/+$/, "");
  const trimmed = clean.replace(/^\/+/, "");
  return baseUrl ? `${baseUrl}/${trimmed}` : `/${trimmed}`;
};

const detectDocumentType = (
  source?: string | null,
  name?: string,
  type?: string,
): "image" | "pdf" | "other" => {
  if (type) {
    const lowerType = type.toLowerCase();
    if (lowerType.startsWith("image/")) return "image";
    if (lowerType === "application/pdf") return "pdf";
  }

  if (source && source.startsWith("data:")) {
    if (source.startsWith("data:image/")) return "image";
    if (source.startsWith("data:application/pdf")) return "pdf";
  }

  const target = (name || source || "").split("?")[0].split("#")[0];
  const ext = target.split(".").pop()?.toLowerCase() || "";

  const imageExtensions = [
    "jpg",
    "jpeg",
    "png",
    "webp",
    "gif",
    "svg",
    "bmp",
    "ico",
    "jfif",
    "avif",
  ];

  if (imageExtensions.includes(ext)) {
    return "image";
  }

  if (ext === "pdf") {
    return "pdf";
  }

  return "other";
};

const DocumentPopUp = ({
  showDoc,
  setShowDoc,
  docs,
  url,
  fileName,
  fileType,
}: DocumentPopUpProps) => {
  if (!showDoc) return null;

  const rawSource = url || docs;
  const fileUrl = resolveFileUrl(rawSource);
  const docType = detectDocumentType(rawSource, fileName, fileType);
  const displayName =
    fileName || (rawSource ? rawSource.split("/").pop() : "Document");

  return (
    <div className="doc-modal-overlay" onClick={() => setShowDoc(false)}>
      <div className="doc-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Body */}
        <div
          className="doc-modal-body"
          style={{
            flex: 1,
            overflowY: "auto",
            minHeight: "250px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!fileUrl ? (
            <p className="text-muted mb-0">No document available</p>
          ) : docType === "image" ? (
            <img
              src={fileUrl}
              alt={displayName || "Document"}
              className="w-100"
              style={{
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          ) : docType === "pdf" ? (
            <iframe
              src={fileUrl}
              title={displayName || "PDF Document"}
              width="100%"
              height="100%"
              style={{
                minHeight: "70vh",
                border: "none",
                borderRadius: "6px",
                width: "100%",
              }}
            />
          ) : (
            <div className="text-center py-4 my-auto w-100">
              <img
                src={getDocumentFileIcon(displayName || "")}
                alt="File preview"
                style={{ width: "64px", height: "64px", objectFit: "contain" }}
                className="mb-3"
              />
              <h6
                className="mb-2 text-truncate px-3 font-weight-bold"
                style={{ maxWidth: "450px", margin: "0 auto" }}
              >
                {displayName}
              </h6>
              <p className="text-muted mb-3 small">
                Preview is not available for this file type in the browser.
              </p>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="btn btn-badge-primary px-4"
                style={{ color: "var(--white)" }}
              >
                <Download size={16} className="me-2" />
                Download
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="d-flex justify-content-end align-items-center mt-3 pt-2">
          <button
            type="button"
            className="btn btn-badge-primary px-5"
            onClick={() => setShowDoc(false)}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentPopUp;
